import _ from 'underscore';
import { getLenisInstance } from '../core/scroll-manager.js';

/**
 * Manages accordion components, including height calculation, toggling,
 * group behavior, and deep-linking via URL hash.
 *
 * @param {boolean} log - Whether to log debug messages.
 * @returns {object} An object with a `destroy` method to clean up event listeners.
 */
export function initializeAccordions(log = false) {
	const accordions = Array.from(document.querySelectorAll('.accordion'));
	if (accordions.length === 0) {
		if (log) console.log('Accordions: No .accordion elements found on this page.');
		return { destroy: () => {} }; // Return a no-op destroy function
	}

	/**
	 * Calculates and sets the height of each accordion's content area.
	 * This is stored in a data attribute for use in CSS transitions.
	 */
	const calculateAllAccordionHeights = () => {
		accordions.forEach((accordion) => {
			const contentWrapper = accordion.querySelector('.accordion__content');
			const contentInner = contentWrapper ? contentWrapper.querySelector('div') : null;
			if (contentWrapper && contentInner) {
				contentWrapper.dataset.height = contentInner.offsetHeight;
			}
		});
		if (log) console.log('Accordions: Recalculated heights.');
	};

	// Debounced version for resize events
	const debouncedCalculateHeights = _.debounce(calculateAllAccordionHeights, 150);

	/**
	 * Handles the click event on an accordion title.
	 * @param {Event} e - The click event.
	 */
	const handleAccordionClick = (e) => {
		e.preventDefault();
		const title = e.currentTarget;
		const accordion = title.closest('.accordion');
		if (!accordion) return;

		const content = accordion.querySelector('.accordion__content');
		const groupId = accordion.dataset.groupId;

		const isOpening = !accordion.classList.contains('expand');

		// If a group ID exists, close other accordions in the same group.
		if (groupId) {
			const otherAccordionsInGroup = document.querySelectorAll(`.accordion[data-group-id="${groupId}"]`);
			otherAccordionsInGroup.forEach((otherAccordion) => {
				if (otherAccordion !== accordion) {
					otherAccordion.classList.remove('expand');
					otherAccordion.querySelector('.accordion__content').style.height = '0px';
				}
			});
		}

		// Toggle the clicked accordion
		if (isOpening) {
			// Recalculate height just before opening to account for dynamic content.
			const contentInner = content.querySelector('div');
			if (contentInner) {
				content.dataset.height = contentInner.offsetHeight;
			}
			accordion.classList.add('expand');
			content.style.height = `${content.dataset.height}px`;
		} else {
			accordion.classList.remove('expand');
			content.style.height = '0px';
		}

		// --- Lenis Recalculation ---
		// The document height changes during the accordion's CSS transition.
		// We must tell Lenis to recalculate its dimensions *after* the transition completes.
		// A simple timeout is unreliable; 'transitionend' is the correct event.
		content.addEventListener('transitionend', function onTransitionEnd() {
			const lenis = getLenisInstance();
			if (lenis) {
				lenis.resize();
			}

			// IMPORTANT: Remove the event listener after it has fired once to prevent
			// it from running on every subsequent transition (e.g., color changes).
			content.removeEventListener('transitionend', onTransitionEnd);
		}, { once: true }); // The `{ once: true }` option is a modern way to auto-remove the listener.

	};

	/**
	 * Checks the URL hash and opens the corresponding accordion.
	 */
	const openAccordionFromHash = () => {
		const hash = window.location.hash;
		if (!hash) return;

		const targetId = hash.substring(1);
		const targetAccordion = document.querySelector(`.accordion[data-target="${targetId}"]`);

		if (targetAccordion) {
			// Open the accordion
			const content = targetAccordion.querySelector('.accordion__content');
			targetAccordion.classList.add('expand');
			content.style.height = `${content.dataset.height}px`;

			// Smooth scroll to it using Lenis if available
			const lenis = getLenisInstance();
			const header = document.getElementById('header');
			const offset = header ? -header.offsetHeight : 0;

			// Use a timeout to ensure the page has settled after Barba transition
			setTimeout(() => {
				if (lenis) {
					lenis.scrollTo(targetAccordion, { offset });
				} else {
					// Fallback to native scroll if Lenis isn't active
					const targetTop = targetAccordion.getBoundingClientRect().top + window.scrollY + offset;
					window.scrollTo({ top: targetTop, behavior: 'smooth' });
				}
			}, 150); // Small delay for stability
		}
	};

	// --- Initialization ---
	calculateAllAccordionHeights();
	accordions.forEach((accordion) => {
		const title = accordion.querySelector('.accordion__title');
		if (title) {
			title.addEventListener('click', handleAccordionClick);
		}
	});
	window.addEventListener('resize', debouncedCalculateHeights);
	window.addEventListener('orientationchange', debouncedCalculateHeights);
	openAccordionFromHash();

	if (log) console.log('Accordions: Initialized.');

	// Return a cleanup function
	return {
		destroy: () => {
			accordions.forEach((accordion) => {
				const title = accordion.querySelector('.accordion__title');
				if (title) {
					title.removeEventListener('click', handleAccordionClick);
				}
			});
			window.removeEventListener('resize', debouncedCalculateHeights);
			window.removeEventListener('orientationchange', debouncedCalculateHeights);
			if (log) console.log('Accordions: Destroyed listeners.');
		}
	};
}